import React, { useState } from "react";
import { FileText } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { cn } from "@/lib/utils";
import { TemplatesDialog, type MessageTemplate } from "./TemplatesDialog";

export interface FilledTemplate {
  subject: string | null;
  body: string;
  missing: string[];
}

/**
 * "Templates": the person's own and shared message templates for one channel. Picking one fills it in for the record being written to
 * (merge fields come from the record) and hands the text back; fields with no value are left as {{field}} and named in a warning.
 */
export function TemplatePicker({
  channel,
  entity,
  recordId,
  onPick,
  disabled,
  className,
  children,
}: {
  channel: "EMAIL" | "WHATSAPP";
  entity: "Lead" | "Deal" | "Contact" | "Account";
  recordId?: string | null;
  onPick: (filled: FilledTemplate) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  const { error, info } = useToast();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<MessageTemplate[]>([]);
  const [manage, setManage] = useState(false);
  const [busy, setBusy] = useState("");

  const load = () =>
    api
      .get(`/api/MessageTemplate/List?channel=${channel}`)
      .then((r) => setRows(unwrapArray<MessageTemplate>(r)))
      .catch(() => undefined);

  const pick = async (t: MessageTemplate) => {
    if (!recordId) return;
    setBusy(t.id);
    try {
      const filled = unwrapObject<FilledTemplate>(await api.post("/api/MessageTemplate/Render", { template_id: t.id, entity, id: recordId }));
      onPick(filled);
      setOpen(false);
      if (filled.missing?.length) info("Some fields have no value", `${filled.missing.map((m) => `{{${m}}}`).join(", ")} were left in the text: fill them in before sending.`);
    } catch (e: any) {
      error("Couldn't use the template", e?.message || "Try again");
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (o) void load();
        }}
      >
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} title="Templates" aria-label="Templates" className={cn("inline-flex cursor-pointer items-center gap-1.5 disabled:opacity-50", className)}>
            {children || (
              <>
                <FileText className="size-4" /> Templates
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <div className="max-h-72 overflow-y-auto py-1 text-sm">
            {rows.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">No templates yet.</p>}
            {rows.map((t) => (
              <button key={t.id} type="button" disabled={busy === t.id || !recordId} onClick={() => pick(t)} className="flex w-full flex-col px-3 py-1.5 text-left hover:bg-muted disabled:opacity-60">
                <span className="truncate font-medium">{t.name}</span>
                <span className="line-clamp-1 text-xs text-muted-foreground">{t.body}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setManage(true);
            }}
            className="w-full border-t border-border px-3 py-2 text-left text-xs font-medium text-primary hover:bg-muted"
          >
            Manage templates…
          </button>
        </PopoverContent>
      </Popover>
      <TemplatesDialog open={manage} onClose={() => setManage(false)} channel={channel} onChanged={load} />
    </>
  );
}
